<template>
  <div class="payment-display-container">
    <div class="header-logo-container mt-4"></div>
    <div class="main-content">
      <h2 class="page-title">Please make payment to</h2>
      <div class="payment-card">
        <div class="payment-details">
          <div class="header">
            <span class="time-remaining-label">Time Remaining</span>
            <span class="time-remaining-value">{{
              formattedTimeRemaining
            }}</span>
          </div>
          <div class="bank-info">
            <img
              :src="bankLogoUrl"
              :alt="paymentDetails.bankName"
              class="bank-logo"
            />
            <!-- Removed Account Name from here as it's not in the image's left panel -->
          </div>
          <div class="detail-item account-name-item">
            <span class="detail-label">Account Name</span>
            <div class="detail-value">
              <span>{{ paymentDetails.accountName }}</span>
              <!-- No copy button for account name in the image -->
            </div>
          </div>
          <div class="detail-item">
            <span class="detail-label">No. Virtual Account</span>
            <div class="detail-value">
              <span>{{ displayVirtualAccountNumber }}</span>
              <button
                @click="
                  copyToClipboard(
                    displayVirtualAccountNumber,
                    'Virtual Account Number'
                  )
                "
                class="copy-button"
              >
                Copy
              </button>
            </div>
          </div>
          <div class="detail-item">
            <span class="detail-label">Total Amount</span>
            <div class="detail-value">
              <span>IDR {{ formatCurrency(paymentDetails.totalAmount) }}</span>
              <button
                @click="
                  copyToClipboard(paymentDetails.totalAmount, 'Total Amount')
                "
                class="copy-button"
              >
                Copy
              </button>
            </div>
          </div>
          <div class="actions">
            <button
              class="btn btn-success btn-block check-status-btn"
              @click="checkPaymentStatus"
            >
              Check Payment Status
            </button>
            <!-- <button
              class="btn btn-outline-secondary btn-block change-method-btn"
              @click="changePaymentMethod"
            >
              Change Payment Method
            </button> -->
            <!-- <a href="#" @click.prevent="done" class="done-link">Done</a> -->
          </div>
        </div>
        <div class="how-to-pay">
          <h3 class="how-to-pay-title">How to pay</h3>
          <div class="accordion">
            <div
              class="accordion-item"
              v-for="(instruction, key) in paymentInstructions"
              :key="key"
            >
              <button
                class="accordion-header"
                @click="toggleAccordion(key)"
                :aria-expanded="activeAccordion === key"
              >
                {{ instruction.title }}
                <span class="accordion-icon">
                  <svg
                    v-if="activeAccordion === key"
                    width="12"
                    height="2"
                    viewBox="0 0 12 2"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1H11"
                      stroke="#666666"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <svg
                    v-else
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 1V11"
                      stroke="#666666"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M1 6H11"
                      stroke="#666666"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </button>
              <div class="accordion-content" v-show="activeAccordion === key">
                <ul>
                  <li
                    v-for="(step, index) in instruction.steps"
                    :key="index"
                    v-html="step"
                  ></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="footer-text text-center">
      <p>
        &copy; PT Anugrah Multi Remittance {{ new Date().getFullYear() }}
        <span class="footer-separator">|</span>
        <a href="#">Term & Conditions</a>
        <span class="footer-separator">|</span> <a href="#">Privacy Policy</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { checkVirtualAccountStatus } from "@/api/transaction";
import Swal from "sweetalert2";

const router = useRouter();
const store = useStore();

store.state.hideConfigButton = false;
store.state.showTransferNavbar = true;
store.state.showNavbar = false;
store.state.showSidenav = false;
store.state.showFooter = false;

// Get state from router
const routeState = computed(() => router.currentRoute.value.state || {});

// Payment details with proper type checking
const paymentDetails = ref({
  bankName: routeState.value?.paymentDetails?.bankName || "",
  accountName: routeState.value?.paymentDetails?.accountName || "",
  virtualAccountNumber:
    routeState.value?.paymentDetails?.virtualAccountNumber || "",
  totalAmount: routeState.value?.paymentDetails?.totalAmount || 0,
  bankLogo: routeState.value?.paymentDetails?.bankLogo || "",
  batchId: routeState.value?.paymentDetails?.batchId || null,
});

// Computed property for display VA number with prefix logic
const displayVirtualAccountNumber = computed(() => {
  const baseVaNumber = paymentDetails.value.virtualAccountNumber;
  const bankName = paymentDetails.value.bankName || "";
  
  // Check if bank is Mandiri
  const isMandiri = bankName.toLowerCase().includes('mandiri');
  
  // For non-Mandiri banks, add 88017 prefix
  if (!isMandiri && baseVaNumber) {
    return `${baseVaNumber}`;
  }
  
  return baseVaNumber;
});

// Handle expiry time
const expiresAt = ref(routeState.value?.expiresAt || null);
const timeRemaining = ref(0);
let timerInterval = null;

// Bank logos mapping
const bankLogos = {
  BCA: require("@/assets/img/logos/bank-bca.png"),
  PERMATA: require("@/assets/img/logos/bank-permata.png"),
  BNI: require("@/assets/img/logos/bank-bni.png"),
  BRI: require("@/assets/img/logos/bank-bri.png"),
  MANDIRI: require("@/assets/img/logos/bank-mandiri.png"),
};

// Bank name mapping
const bankNameMapping = {
  "Bank Central Asia": "BCA",
  "Bank Permata": "PERMATA",
  "Bank Negara Indonesia": "BNI",
  "Bank Rakyat Indonesia": "BRI",
  "Bank Mandiri": "MANDIRI",
};

const bankLogoUrl = computed(() => {
  const fullBankName = paymentDetails.value.bankName;
  const bankKey = bankNameMapping[fullBankName] || "BCA"; // Default to BCA if bank not found
  return bankLogos[bankKey];
});

// Calculate time remaining
const calculateTimeRemaining = () => {
  if (!expiresAt.value) return 0;

  const now = new Date().getTime();
  const expiry = new Date(expiresAt.value).getTime();
  const remaining = Math.max(0, Math.floor((expiry - now) / 1000));

  if (remaining <= 0) {
    clearInterval(timerInterval);
    store.dispatch("showToast", {
      type: "warning",
      message: "Payment session has expired",
    });
    router.push({ name: "PaymentMethod" });
  }

  return remaining;
};

const formattedTimeRemaining = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60);
  const seconds = timeRemaining.value % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
});

// Initialize timer
onMounted(() => {
  if (expiresAt.value) {
    timeRemaining.value = calculateTimeRemaining();
    timerInterval = setInterval(() => {
      timeRemaining.value = calculateTimeRemaining();
    }, 1000);
  }
});

// Cleanup timer
onUnmounted(() => {
  if (timerInterval) {
    clearInterval(timerInterval);
  }
});

const activeAccordion = ref("ATM");

const paymentInstructions = ref({
  ATM: {
    title: "ATM",
    steps: [],
  },
  mBanking: {
    title: "Mobile Banking",
    steps: [],
  },
});

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "0,00";
  return Number(value).toLocaleString("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const copyToClipboard = async (text, itemLabel) => {
  try {
    await navigator.clipboard.writeText(String(text));
    Swal.fire({
      icon: "success",
      title: `${itemLabel} copied to clipboard!`,
    });
  } catch (err) {
    console.error("Failed to copy: ", err);
    Swal.fire({
      icon: "error",
      title: `Failed to copy ${itemLabel}.`,
    });
  }
};

const checkPaymentStatus = async () => {
  try {
    const response = await checkVirtualAccountStatus(
      paymentDetails.value.virtualAccountNumber
    );


    if (response.flag === "paid") {
      Swal.fire({
        icon: "success",
        title: "Payment successful!",
        text: response.message,
      });
      router.push({
        name: "PaymentSuccess",
        state: {
          batchId: paymentDetails.value.batchId,
          totalAmount: paymentDetails.value.totalAmount,
        },
      });
    } else if (response.flag === 'cancelled') {
      router.push({
        name: "PaymentCancelled",
        state: {
          batchId: paymentDetails.value.batchId,
          totalAmount: paymentDetails.value.totalAmount,
        },
      });
    }
    else if (response.flag === "expired") {
      Swal.fire({
        icon: "warning",
        title: "Payment is expired",
        text: response.message,
      });
    } else if (response.flag === "not_paid") {
      Swal.fire({
        icon: "error",
        title: "Payment is processing",
        text: response.message,
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Payment is failed",
        text: response.message,
      });
    }


  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Failed to check payment status",
      text: error.message,
    });
  }
};

// const changePaymentMethod = () => {
//   // Pass batchId as query parameter so Transfer can fetch the summary
//   router.push({ 
//     name: "Transfer", 
//     query: { 
//       currentStep: 4,
//       batchId: paymentDetails.value.batchId 
//     }
//   });
// };

// const done = () => {
//   router.push({ name: "Home" });
// };

const toggleAccordion = (key) => {
  activeAccordion.value = activeAccordion.value === key ? null : key;
};

const updatePaymentInstructionsForBank = (bankName) => {
  if (!bankName) return;

  // Check bank type
  const isMandiri = bankName.toLowerCase().includes('mandiri');
  const isPermata = bankName.toLowerCase().includes('permata');
  const actualVA = displayVirtualAccountNumber.value;
  
  if (isMandiri) {
    // Mandiri specific instructions
    paymentInstructions.value.ATM.steps = [
      'Go to Bank Mandiri ATM',
      'Select "Pay" menu',
      'Search for I-PAY or type 70017',
      'Enter the VA number in the reference code field',
      'Enter the amount and complete payment'
    ];
    paymentInstructions.value.mBanking.steps = [
      'Open Livin by Mandiri app',
      'Select "Pay" menu',
      'Search for I-PAY or type 70017',
      'Enter the VA number in the reference code field',
      'Enter the amount and complete payment'
    ];
  } else if (isPermata) {
    // Permata specific instructions - transfer online to Bank Permata
    paymentInstructions.value.ATM.steps = [
      'Insert your ATM card and enter your PIN',
      'Select "Transfer" menu',
      'Select "Online Transfer"',
      'Select Bank Permata as the destination bank',
      `Enter the Virtual Account number: ${actualVA}`,
      'Enter the amount you want to transfer',
      'Confirm the transaction and complete'
    ];
    paymentInstructions.value.mBanking.steps = [
      'Open your Mobile Banking app',
      'Select "Transfer" menu',
      'Select "Online Transfer" or "Transfer to Other Bank"',
      'Select Bank Permata as the destination bank',
      `Enter the Virtual Account number: ${actualVA}`,
      'Enter the amount you want to transfer',
      'Confirm the transaction and complete'
    ];
  } else {
    // For other banks - transfer to Mandiri account with 88017 prefix
    paymentInstructions.value.ATM.steps = [
      'Insert your ATM card and enter your PIN',
      'Select "Transfer" menu',
      'Select "Transfer to Other Bank" or "Interbank Transfer"',
      'Select Bank Mandiri as the destination bank',
      `Enter the account number: 88017 + VA number (example: 88017${actualVA})`,
      'Enter the amount you want to transfer',
      'Confirm the transaction and complete'
    ];
    paymentInstructions.value.mBanking.steps = [
      'Open your Mobile Banking app',
      'Select "Transfer" menu',
      'Select "Transfer to Other Bank"',
      'Select Bank Mandiri as the destination bank',
      `Enter the account number: 88017 + VA number (example: 88017${actualVA})`,
      'Enter the amount you want to transfer',
      'Confirm the transaction and complete'
    ];
  }

  // Ensure the active accordion is still valid or reset it
  if (!paymentInstructions.value[activeAccordion.value]) {
    activeAccordion.value = Object.keys(paymentInstructions.value)[0] || null;
  }
};

// Watch for changes in payment details to update instructions
watch(
  () => paymentDetails.value.bankName,
  (newBankName) => {
    if (newBankName) {
      updatePaymentInstructionsForBank(newBankName);
    }
  },
  { immediate: true }
);

// Watch for changes in displayVirtualAccountNumber to update instructions
watch(
  displayVirtualAccountNumber,
  () => {
    updatePaymentInstructionsForBank(paymentDetails.value.bankName);
  }
);

onMounted(() => {
  const state = history.state;
  if (state && state.paymentDetails) {
    paymentDetails.value = {
      ...paymentDetails.value,
      ...state.paymentDetails,
    };
    updatePaymentInstructionsForBank(paymentDetails.value.bankName);
  } else {
    console.warn(
      "VirtualAccountDisplay: Payment details not found in history.state."
    );
    // Fallback or redirect if needed
    updatePaymentInstructionsForBank(paymentDetails.value.bankName); // Use default BCA if no state
  }

  if (state && state.expiresAt) {
    const now = Math.floor(Date.now() / 1000);
    const expiry = Math.floor(new Date(state.expiresAt).getTime() / 1000);
    timeRemaining.value = Math.max(0, expiry - now);
  } else {
    // Set default time if not provided, e.g., from the image 00:29:58
    timeRemaining.value = 29 * 60 + 58;
  }

  timerInterval = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value -= 1;
    } else {
      clearInterval(timerInterval);
      store.dispatch("showToast", {
        type: "warning",
        message: "Payment time has expired.",
      });
    }
  }, 1000);
});

onUnmounted(() => {
  clearInterval(timerInterval);
});
</script>

<style scoped>
/* Ensure you have a global font set, or set it here. Example: Inter */
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

.payment-display-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  background-color: #f7fafc; /* Light green-ish grey from image */
  padding: 20px;
  font-family: "Inter", sans-serif; /* Matching the font from image */
}

.header-logo-container {
  width: 100%;
  max-width: 1140px; /* Standard container width */
  margin-bottom: 40px; /* Space between logo and title */
  padding-left: 15px; /* Align with typical container padding */
}

.header-logo {
  height: 32px; /* Adjust based on your logo's aspect ratio and desired size */
}

.main-content {
  width: 100%;
  max-width: 1000px; /* Increased from 860px */
}

.page-title {
  font-size: 24px; /* Matches image */
  font-weight: 700; /* Bold */
  color: #1a202c; /* Darker text color */
  margin-bottom: 24px; /* Space below title */
  text-align: left;
}

.payment-card {
  display: flex;
  background-color: #fff;
  border-radius: 12px; /* Slightly more rounded corners */
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.05); /* Softer shadow */
  padding: 24px; /* Uniform padding */
  gap: 24px; /* Gap between left and right panels */
}

.payment-details {
  flex: 1;
  padding-right: 0;
}

.payment-details .header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0; /* Lighter border */
}

.time-remaining-label {
  font-size: 14px; /* Matches image */
  color: #4a5568; /* Grey text */
  font-weight: 500;
}

.time-remaining-value {
  font-size: 16px; /* Matches image */
  font-weight: 700; /* Bold */
  color: #dd6b20; /* Orange color for timer */
}

.bank-info {
  display: flex;
  align-items: center;
  margin-bottom: 16px; /* Space below bank logo */
}

.bank-logo {
  height: 24px; /* Adjusted size */
  margin-right: 8px; /* Reduced margin */
}

.account-name-item .detail-label,
.account-name-item .detail-value span {
  font-size: 14px; /* Consistent font size */
}

.account-name-item .detail-value span {
  font-weight: 600; /* Bold as in image */
  color: #2d3748;
}

.detail-item {
  margin-bottom: 16px;
}

.detail-label {
  display: block;
  font-size: 12px; /* Smaller label text */
  color: #718096; /* Lighter grey */
  margin-bottom: 4px;
  font-weight: 500;
}

.detail-value {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px; /* Larger value text */
  font-weight: 700; /* Bold */
  color: #2d3748; /* Darker text */
}

.detail-value span:first-child {
  word-break: break-all;
}

.copy-button {
  background: none;
  border: none;
  color: #3182ce; /* Blue color for copy */
  cursor: pointer;
  font-size: 14px; /* Matches other text */
  font-weight: 500;
  margin-left: 8px;
  padding: 4px;
}

.actions {
  margin-top: 24px; /* Space above buttons */
}

.actions .btn {
  margin-bottom: 12px;
  padding: 10px 16px; /* Adjusted padding */
  font-size: 14px;
  font-weight: 600; /* Medium bold */
  border-radius: 6px; /* Consistent border radius */
  width: 100%; /* Make buttons full width */
  height: 44px; /* Fixed height for buttons */
  display: flex;
  align-items: center;
  justify-content: center;
}

.check-status-btn {
  background-color: #38a169; /* Green from image */
  border-color: #38a169;
  color: #fff;
}
.check-status-btn:hover {
  background-color: #2f855a;
  border-color: #2f855a;
}

.change-method-btn {
  background-color: #fff;
  border: 1px solid #cbd5e0; /* Light grey border */
  color: #4a5568; /* Grey text */
}
.change-method-btn:hover {
  background-color: #f7fafc;
  border-color: #a0aec0;
}

.done-link {
  display: block;
  text-align: center;
  color: #3182ce; /* Blue link */
  margin-top: 16px; /* Space above done link */
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
}
.done-link:hover {
  text-decoration: underline;
}

.how-to-pay {
  flex: 1;
  min-width: 300px; /* Ensure it has some minimum width */
}

.how-to-pay-title {
  font-size: 16px; /* Matches image */
  font-weight: 600; /* Medium bold */
  color: #2d3748;
  margin-bottom: 16px;
}

/* Accordion styles from the previous version (before SVG icons and specific border/radius changes) */
.accordion-item {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
}

.accordion-header {
  background-color: #f8f9fa;
  padding: 15px;
  width: 100%;
  text-align: left;
  border: none;
  font-size: 1rem; /* Reverted from 14px */
  font-weight: 500; /* Reverted from 600 */
  color: #333; /* Reverted from #2D3748 */
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Removed specific border-radius for header here, relies on .accordion-item */
  /* Removed border-bottom here, relies on .accordion-item or .accordion-content for separation */
}

.accordion-header:hover {
  background-color: #f1f3f5;
}

.accordion-icon {
  font-size: 1.2rem; /* Reverted */
  /* color: #A0AEC0; Reverted, will inherit from header or use default */
}

.accordion-content {
  padding: 15px; /* Reverted from 16px */
  background-color: #fff;
  border-top: 1px solid #e0e0e0; /* Reverted: Added back border-top for content */
  /* Removed specific border-radius for content here */
}

.accordion-content ul {
  list-style-type: disc; /* Reverted to default disc */
  padding-left: 20px; /* Reverted */
  margin: 0;
  font-size: 0.9rem; /* Reverted from 14px */
  color: #555; /* Reverted */
  line-height: normal; /* Reverted from 1.6 */
}

.accordion-content ul li {
  margin-bottom: 8px;
  padding-left: 0; /* Reverted: Removed custom bullet padding */
  position: static; /* Reverted */
}

.accordion-content ul li::before {
  /* Removed custom bullet styling */
  content: "";
  display: none;
}

.accordion-content ul li:last-child {
  margin-bottom: 0;
}
/* End of reverted accordion styles */

.footer-text {
  font-size: 12px; /* Smaller footer text */
  color: #718096; /* Lighter grey */
  padding-top: 40px; /* More space above footer */
  padding-bottom: 20px;
  width: 100%;
  text-align: center;
}

.footer-text a {
  color: #718096; /* Match text color */
  text-decoration: none;
}

.footer-text a:hover {
  text-decoration: underline;
  color: #2d3748;
}

.footer-separator {
  margin: 0 5px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .payment-card {
    flex-direction: column;
    padding: 16px;
  }
  .payment-details {
    border-right: none;
    padding-right: 0;
    margin-bottom: 0;
    padding-bottom: 0;
  }
  .page-title {
    font-size: 20px;
  }
  .header-logo-container {
    margin-bottom: 20px;
    display: flex;
    justify-content: center;
    padding-left: 0;
  }
  .main-content {
    max-width: 100%;
  }
}
</style>
